// PoBillingPanel.tsx — the PO page's billing rail (financial only).
//
// Every invoice that bills this order, in one column beside the document: what
// it bills here, its own total when that differs, due dates with overdue
// derived on the spot, the lifecycle state, and the bookkeeper's payment note
// (Open / Partial / Paid plus the hold) editable in place.
//
// The rail answers her four questions in order: what invoices exist against
// this order, which are still open, which are safe to pay (received coverage,
// the third leg of the three-way match), and which she has already paid.
//
// VISIBILITY: the whole rail is can_financial (Mannie, 2026-08-24) — the page
// mounts it only for financial users, and its data route sits behind the same
// strict gate server-side. Buyers' view of billing stays the match board.
//
// A payment save hands the server's own row UP through onInvoiceSaved; the
// page folds it into its billing state locally. No refetch rides a note save:
// the note changes nothing else on this read, and the refetch it used to fire
// was a race (two quick saves resolving out of order) and a failure mode (a
// failed refetch blanking the rail) for zero information.
//
// Amounts here are the invoice's own figures. "Bills this PO" is this order's
// slice; a row whose total is bigger names the other orders, because summing
// the total column across pages double-counts and that trap is cheaper to
// label than to explain.

import React from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { Progress } from '../components/ui/Progress';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import { formatEventDate } from '../utils/date-utils';
import PaymentNote from '../AccountsPayable/PaymentNote';
import {
  effectiveDueDate,
  formatCents,
  isOverdue,
  PAYMENT_STATUS_LABELS,
  type InvoiceStatus,
  type OrderBilling,
  type OrderInvoice,
  type VendorInvoice,
} from '../AccountsPayable/apInvoicesApi';

/** Lifecycle, in the queue's own words. Discarded rows never reach this panel. */
const LIFECYCLE_LABELS: Partial<Record<InvoiceStatus, string>> = {
  needs_review: 'In review',
  confirmed: 'Confirmed',
  delivered: 'Forwarded',
  delivery_failed: 'Send failed',
  void: 'Void',
};

const PAYMENT_TONES: Record<string, 'neutral' | 'warn' | 'good'> = {
  open: 'neutral',
  partial: 'warn',
  paid: 'good',
};

const InvoiceCard: React.FC<{
  invoice: OrderInvoice;
  onInvoiceSaved: (updated: VendorInvoice) => void;
}> = ({ invoice, onInvoiceSaved }) => {
  const overdue = isOverdue(invoice);
  const dueDate = effectiveDueDate(invoice);
  const spansOthers = invoice.other_po_numbers.length > 0;
  const isVoid = invoice.status === 'void';
  const flaggedReasons = invoice.needs_attention
    ? (invoice.attention_reasons || []).map((r) => r.message).join(' ')
    : '';

  return (
    <Panel
      on="panel"
      padding="sm"
      label={`Invoice ${invoice.invoice_number || 'unnumbered'}`}
      className={`space-y-2 ${isVoid ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to={`/invoices/${invoice.id}`}
          className={`font-mono text-sm text-primary hover:underline whitespace-nowrap ${
            isVoid ? 'line-through' : ''
          }`}
        >
          {invoice.invoice_number || 'Unnumbered'}
        </Link>
        <Appear show={!!invoice.is_credit_memo}>
          <Tag size="sm" tone="info">Credit</Tag>
        </Appear>
        <Tag size="sm" tone="neutral">{LIFECYCLE_LABELS[invoice.status] || invoice.status}</Tag>
        {invoice.counted && !invoice.is_credit_memo && (
          <Tag size="sm" tone={PAYMENT_TONES[invoice.payment_status]}>
            {PAYMENT_STATUS_LABELS[invoice.payment_status]}
          </Tag>
        )}
        <Appear show={!!overdue}>
          <Tag size="sm" tone="bad">Overdue</Tag>
        </Appear>
        <Appear show={!!invoice.payment_on_hold}>
          <Tag size="sm" tone="warn">On hold</Tag>
        </Appear>
        {/* Two very different facts wore one chip until now. A return in
            progress is the system working: the goods came, they went back, and
            the vendor owes an answer that closes the gap by itself. Billing for
            goods that never arrived is the one somebody has to chase, so it
            keeps the amber and says what it is. */}
        <Appear show={!!invoice.ahead_of_receiving && !isVoid && invoice.ahead_reason === 'return_pending'}>
          <Tag size="sm" tone="neutral">Return in progress</Tag>
        </Appear>
        <Appear show={!!invoice.ahead_of_receiving && !isVoid && invoice.ahead_reason !== 'return_pending'}>
          <Tag size="sm" tone="warn">Billed beyond received</Tag>
        </Appear>
        {/* Attention that survived confirm (a duplicate smell, a vendor
            mismatch) stays visible where money is about to move. */}
        <Appear show={!!invoice.needs_attention && !isVoid}>
          <Tooltip label={flaggedReasons}>
            <Tag size="sm" tone="warn">Flagged</Tag>
          </Tooltip>
        </Appear>
        {/* The three-way verdict, said affirmatively: confirmed, unpaid, not
            held, not flagged, and receiving covers what it bills. What she
            SHOULD pay, rather than leaving her to negate four warnings in her
            head. A return in progress does not withhold it: the goods came,
            some went back, and the vendor's credit memo nets the money later.
            Neither reference ERP blocks paying a bill over an open return, so
            the two chips sit together, the verdict and the note. */}
        <Appear
          show={!!(
            invoice.counted &&
            !invoice.is_credit_memo &&
            invoice.payment_status !== 'paid' &&
            !invoice.payment_on_hold &&
            !(invoice.ahead_of_receiving && invoice.ahead_reason !== 'return_pending') &&
            !invoice.needs_attention
          )}
        >
          <Tag size="sm" tone="good">Clear to pay</Tag>
        </Appear>
      </div>

      <div className="text-xs text-on-surface-variant">
        {invoice.invoice_date ? formatEventDate(invoice.invoice_date, 'MMM D, YYYY') : 'No date'}
        {' · due '}
        <span className={overdue ? 'text-red-300 font-medium' : ''}>
          {dueDate ? formatEventDate(dueDate, 'MMM D, YYYY') : 'not set'}
        </span>
        {/* The vendor printed no due date and the order's clock filled it in.
            Said so, because a date nobody printed is an inference. */}
        {dueDate && !invoice.due_date && <span className="text-on-surface-variant"> (from terms)</span>}
      </div>

      <div className="text-sm text-on-surface tabular-nums">
        Bills this PO <span className="font-medium">{formatCents(invoice.this_order_cents)}</span>
        {(spansOthers || (invoice.total_cents !== null && invoice.total_cents !== invoice.this_order_cents)) && (
          <span className="text-on-surface-variant">
            {' '}of {formatCents(invoice.total_cents)}
            {spansOthers && <> · also {invoice.other_po_numbers.join(', ')}</>}
          </span>
        )}
      </div>

      <Reveal show={invoice.status === 'needs_review'}>
        <p className="text-xs text-on-surface-variant">
          Awaiting confirm in{' '}
          <Link to={`/invoices/${invoice.id}`} className="text-primary hover:underline">
            the queue
          </Link>
          ; its amounts are a claim until then.
        </p>
      </Reveal>

      {invoice.counted && !invoice.is_credit_memo && (
        <PaymentNote invoice={invoice} onSaved={onInvoiceSaved} />
      )}
    </Panel>
  );
};

const PoBillingPanel: React.FC<{
  billing: OrderBilling | null;
  loading: boolean;
  /** The last fetch failed. With billing still held the rail shows the stale
   *  read (the page toasts); with nothing held it offers the retry itself. */
  error: boolean;
  onRetry: () => void;
  onInvoiceSaved: (updated: VendorInvoice) => void;
}> = ({ billing, loading, error, onRetry, onInvoiceSaved }) => {
  const invoices = billing?.invoices || [];
  const openCount = invoices.filter(
    (i) => i.counted && !i.is_credit_memo && i.payment_status !== 'paid'
  ).length;
  const overdueCount = invoices.filter((i) => isOverdue(i)).length;
  // 150% billed is the one thing this rail
  // exists to say out loud, and a clamped label would read it as "complete".
  const realPct = billing?.percent_billed ?? 0;

  return (
    <Panel
      title="Billing"
      level={2}
      actions={invoices.length > 0 ? (
        <span className="text-xs font-normal text-on-surface-variant">
          {invoices.length} invoice{invoices.length === 1 ? '' : 's'}
          <Appear show={openCount > 0}> · {openCount} open</Appear>
          <Appear show={overdueCount > 0} className="text-red-300"> · {overdueCount} overdue</Appear>
        </span>
      ) : undefined}
      className="space-y-3"
    >
      <Reveal show={!!billing}>
        <Progress
          value={realPct}
          max={100}
          label="Billed"
          display="percent"
          unit="billed"
          valueText={`${realPct}% billed`}
          success
          over="Over billed"
        />
      </Reveal>

      <Reveal show={loading && !billing}>
        <SkeletonGroup label="Loading billing" className="space-y-2">
          <Skeleton className="h-16 w-full" />
        </SkeletonGroup>
      </Reveal>

      <Alert
          show={error && !billing}
          tone="error"
          variant="note"
          onRetry={onRetry}
          retryTip="Read the billing again"
        >
          The billing read failed, so this rail cannot say anything yet.
        </Alert>

      <Reveal show={!loading && !error && !!billing && invoices.length === 0}>
        <EmptyState variant="line" dense>
          No invoices bill this order yet. When one arrives, upload it with the
          Upload invoice button and it lands here linked; billing rolls on when
          accounting confirms it.
        </EmptyState>
      </Reveal>

      {invoices.map((inv) => (
        <InvoiceCard key={inv.id} invoice={inv} onInvoiceSaved={onInvoiceSaved} />
      ))}
    </Panel>
  );
};

export default PoBillingPanel;
